import { Reward } from '@lunit/shared-types';
import { api } from './client';

export async function getRewards(userTier?: string): Promise<Reward[]> {
  const params = new URLSearchParams();
  params.append('isActive', 'true');
  params.append('isVisible', 'true');
  if (userTier) {
    params.append('userTier', userTier);
    console.log('[rewards.ts] Requesting rewards for tier:', userTier);
  }
  return api.get<Reward[]>(`/rewards?${params.toString()}`);
}

export async function getReward(id: string): Promise<Reward> {
  return api.get<Reward>(`/rewards/${id}`);
}

// ============================================================================
// Two-Step Redemption API
// ============================================================================

export interface RedemptionRequest {
  code: string;
  redemptionId: string;
  barcode: string | null;
  expiresAt: string;
  rewardName: string;
  pointsCost: number;
}

export interface RedemptionStatus {
  status: 'PENDING' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';
  expiresAt: string;
  rewardName: string;
  isExpired: boolean;
  posOrderStatus?: 'CREATED' | 'ACCEPTED' | 'CANCELLED' | null;
  posOrderError?: string | null;
}

/** Ответ отмены резерва клиентом (K-012). status EXPIRED — срок вышел раньше, баллы вернулись экспирацией. */
export interface RedemptionCancelResult {
  success: boolean;
  status: 'CANCELLED' | 'EXPIRED';
  /** Баллы, возвращённые за резерв (ровно один раз, сколько бы раз ни нажали). */
  refunded: number;
  /** Баланс после возврата; null — сервер не смог прочитать. */
  balance: number | null;
  /** Резерв был отменён/истёк до этого запроса (повтор). */
  alreadyFinal: boolean;
}

/**
 * Step 1: Request a redemption code
 * Creates a PENDING redemption with a 6-digit code valid for 10 minutes
 *
 * NEW ENDPOINT: POST /api/v1/redemptions
 * Body: { rewardId, userId }
 */
export async function requestRedemption(
  rewardId: string,
  userId: string
): Promise<RedemptionRequest> {
  console.log('[rewards.ts] requestRedemption called', {
    rewardId,
    userId,
    endpoint: '/redemptions'
  });

  try {
    const result = await api.post<RedemptionRequest>('/redemptions', { rewardId, userId });
    console.log('[rewards.ts] requestRedemption SUCCESS', result);
    return result;
  } catch (error) {
    console.error('[rewards.ts] requestRedemption FAILED', {
      error,
      rewardId,
      userId
    });
    throw error;
  }
}

/**
 * Step 2: Confirm redemption (called by cashier or user after receiving)
 * Changes status from PENDING to COMPLETED
 */
export async function confirmRedemption(code: string): Promise<{
  rewardName: string;
  pointsSpent: number;
  userName: string;
}> {
  return api.post(`/redemptions/confirm`, { code });
}

/**
 * Отмена своего резерва награды (решение владельца №2, K-012): баллы возвращаются сразу.
 * POST /redemptions/{id}/cancel — только по id резерва. silent: исход показывает экран (см. redemptionCancel.ts).
 */
export async function cancelRedemption(redemptionId: string): Promise<RedemptionCancelResult> {
  return api.post<RedemptionCancelResult>(`/redemptions/${redemptionId}/cancel`, {}, { silent: true });
}

/**
 * Check redemption status
 */
export async function getRedemptionStatus(code: string): Promise<RedemptionStatus> {
  return api.get<RedemptionStatus>(`/redemptions/${code}/status`);
}

/**
 * Check redemption status by UUID (for polling auto-confirm from POS)
 */
export async function getRedemptionStatusById(redemptionId: string): Promise<RedemptionStatus> {
  return api.get<RedemptionStatus>(`/redemptions/${redemptionId}/status-by-id`);
}

/**
 * Открытые резервы клиента сессии (PENDING, срок не вышел) — восстановить экран кода с «Отменить»
 * после перезапуска. Бэкенд без этого маршрута отвечает 404 NOT_FOUND — экран прячет отмену.
 */
export async function getOpenRedemptions(): Promise<RedemptionRequest[]> {
  const res = await api.get<{ redemptions: RedemptionRequest[] }>('/users/me/redemptions/open', { silent: true });
  return res?.redemptions ?? [];
}
